import { useState, useRef, useEffect } from 'react';
import NavIcon from './NavIcon';

// ─────────────────────────────────────────────────────────────────────────────
// Why this exists: native <input type="date"> renders completely differently
// per platform, and iOS Safari in particular gives you almost no control over
// its appearance — no reliable way to show a custom icon, inconsistent
// padding, and on some iOS versions no visible calendar affordance at all
// once any custom background/border is applied. Rather than keep patching
// around the native control's quirks, this is a fully custom date field:
// a styled button showing the chosen date, which opens our own calendar
// grid. Nothing here depends on OS-native form-control rendering, so it
// looks identical on an iPhone, an Android phone, and a desktop browser.
//
// Same drop-in contract as before: value is a 'yyyy-mm-dd' string (or ''),
// onChange receives a synthetic { target: { value } } event so existing
// onChange={e => setX(e.target.value)} call sites keep working unchanged.
// ─────────────────────────────────────────────────────────────────────────────

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function toISO(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function parseISO(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return { y, m: m - 1, d };
}
function formatDisplay(str) {
  const parsed = parseISO(str);
  if (!parsed) return null;
  const dt = new Date(parsed.y, parsed.m, parsed.d);
  return `${dt.getDate()} ${MONTHS[dt.getMonth()].slice(0, 3)} ${dt.getFullYear()}`;
}

export default function DateField({ value, onChange, title, placeholder = 'Select date', className = '', style }) {
  const parsed = parseISO(value);
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [viewY, setViewY] = useState(parsed?.y ?? today.getFullYear());
  const [viewM, setViewM] = useState(parsed?.m ?? today.getMonth());
  const wrapRef = useRef(null);

  // Keep the visible month in sync if the value changes from outside
  useEffect(() => {
    const p = parseISO(value);
    if (p) { setViewY(p.y); setViewM(p.m); }
  }, [value]);

  const select = (y, m, d) => {
    onChange({ target: { value: toISO(y, m, d) } });
    setOpen(false);
  };
  const clear = () => { onChange({ target: { value: '' } }); setOpen(false); };
  const goToday = () => select(today.getFullYear(), today.getMonth(), today.getDate());

  const prevMonth = () => {
    if (viewM === 0) { setViewY(viewY - 1); setViewM(11); } else { setViewM(viewM - 1); }
  };
  const nextMonth = () => {
    if (viewM === 11) { setViewY(viewY + 1); setViewM(0); } else { setViewM(viewM + 1); }
  };

  const firstWeekday = new Date(viewY, viewM, 1).getDay();
  const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isSelected = (d) => parsed && parsed.y === viewY && parsed.m === viewM && parsed.d === d;
  const isToday = (d) => today.getFullYear() === viewY && today.getMonth() === viewM && today.getDate() === d;

  return (
    <div ref={wrapRef} className={`date-field-root ${className}`} style={{ position: 'relative', width: '100%', ...style }}>
      <button
        type="button"
        title={title}
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, background: 'var(--surface2)', border: '1px solid var(--border)',
          borderRadius: 7, padding: '9px 12px', color: value ? 'var(--text)' : 'var(--muted)',
          fontFamily: 'var(--sans)', fontSize: 13.5, cursor: 'pointer', boxSizing: 'border-box',
          textAlign: 'left',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {formatDisplay(value) || placeholder}
        </span>
        <span style={{ display: 'flex', flexShrink: 0, color: 'var(--muted)' }}>
          <NavIcon name="calendar" size={15} />
        </span>
      </button>

      {open && (
        <>
          {/* Invisible full-screen tap-to-close backdrop */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 400 }}
          />
          <div
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 401,
              width: 268, background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 8, boxShadow: 'var(--shadow, 0 8px 28px rgba(0,0,0,0.35))',
              padding: 12,
            }}
          >
            {/* Month nav */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <button type="button" onClick={prevMonth} style={navBtnStyle}>‹</button>
              <span style={{ fontFamily: 'var(--display, var(--sans))', fontWeight: 600, fontSize: 13.5, color: 'var(--text)' }}>
                {MONTHS[viewM]} {viewY}
              </span>
              <button type="button" onClick={nextMonth} style={navBtnStyle}>›</button>
            </div>

            {/* Weekday header */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
              {WEEKDAYS.map(w => (
                <div key={w} style={{ textAlign: 'center', fontSize: 10.5, fontWeight: 600, color: 'var(--muted)', padding: '4px 0' }}>{w}</div>
              ))}
            </div>

            {/* Day grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
              {cells.map((d, i) => d === null ? (
                <div key={`b${i}`} />
              ) : (
                <button
                  key={d}
                  type="button"
                  onClick={() => select(viewY, viewM, d)}
                  style={{
                    aspectRatio: '1', border: 'none', borderRadius: 6, cursor: 'pointer',
                    fontFamily: 'var(--mono, var(--sans))', fontSize: 12.5,
                    background: isSelected(d) ? 'var(--accent)' : 'transparent',
                    color: isSelected(d) ? '#fff' : isToday(d) ? 'var(--accent)' : 'var(--text)',
                    fontWeight: isSelected(d) || isToday(d) ? 700 : 400,
                    outline: isToday(d) && !isSelected(d) ? '1px solid var(--accent)' : 'none',
                  }}
                >
                  {d}
                </button>
              ))}
            </div>

            {/* Footer actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              <button type="button" onClick={clear} style={footerBtnStyle}>Clear</button>
              <button type="button" onClick={goToday} style={{ ...footerBtnStyle, color: 'var(--accent)', fontWeight: 600 }}>Today</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const navBtnStyle = {
  width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'none', border: 'none', color: 'var(--accent)', fontSize: 18,
  cursor: 'pointer', borderRadius: 5, lineHeight: 1,
};

const footerBtnStyle = {
  background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12,
  cursor: 'pointer', padding: '4px 8px', fontFamily: 'var(--sans)',
};
